import React, { useState, useContext } from 'react'

import { Link } from 'react-router-dom'
import useStructureParams from '../lib/useStructureParams'
import {
  filterAndSortChildren,
  StructFilterLabelType,
  wardNamesCleanup
} from '../lib/structures'

import PageTitle from './PageTitle'
import Breadcrumbs from './Breadcrumbs'
import PageHeader from './PageHeader'
import ViewButtons from './ViewButtons'
import ResponsiblePersons from './ResponsiblePersons'

import Table from 'react-bootstrap/Table'

import { LoadingSpinner } from './spinners'
import { NoAssignedWardsMessage } from './alerts'
import { formatInt, formatNumAndPerc } from '../lib/utils'

import { Chart as ChartJS, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'
import { DashboardContext } from '../data/context'
import { useActivityAttendance } from '../data/useActivityAttendance'
import PeopleCheckableFilter from './PeopleCheckableFilter'
import NumPercCell from './NumPercCell'
import HeaderLayout from "./HeaderLayout"

ChartJS.register(annotationPlugin, ...registerables)

export default function ActivityAttendanceView (): JSX.Element {
  const { filterLabel } = useContext(DashboardContext)
  const [filterPersonType, setFilterPersonType] =
    useState<StructFilterLabelType[] | undefined>(['public_rep', 'candidate', 'staff'])
  // get parameters from the URL
  const { code, type } = useStructureParams()

  // get stats from the GraphQL API
  const { stats, loading } = useActivityAttendance({
    type,
    code,
    filterLabel,
    filterPersonType
  })
  // this happens while the query is still fetching
  if (stats == null || loading) {
    return <LoadingSpinner />
  }

  // filter/sort children appropriately according to structure type
  const children = filterAndSortChildren(stats.type, stats.children)
    .map(wardNamesCleanup)
    .filter((c) => c.structureHasAssignedWards)
    .sort((a, b) => {
      const getProgress = (compare: typeof a) =>
        compare.activity?.personTypeAttendance.personCount
          ? compare.activity.personTypeAttendance.last30days.within /
          compare.activity.personTypeAttendance.personCount
          : 0
      const progressDiff = getProgress(b) - getProgress(a)

      if (progressDiff !== 0) return progressDiff
      return (a.name ?? '').localeCompare(b.name ?? '')
    })

  if (!stats.structureHasAssignedWards) {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/participation' />
        {/* <PageHeader structure={stats} />
        <ViewButtons structure={stats} section='participation' /> */}
        <NoAssignedWardsMessage type={stats.type} />
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="participation" />}
        />
      </>
    )
  } else {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/participation' />
        {/* <PageHeader structure={stats} />
        <ViewButtons structure={stats} section='participation' /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="participation" />}
        />

        <Table responsive striped className='break-before stats'>
          <thead>
            <tr>
              <th rowSpan={2} className='text-center'>Structure</th>
              <PeopleCheckableFilter filterPersonType={filterPersonType} onChange={setFilterPersonType} />
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center' colSpan={4}>Attendance Inside Constituency</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center' colSpan={4}>Attendance Outside Constituency</th>
            </tr>
            <tr>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Since 14 April 25</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Last 30 Days</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Last 7 Days</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Next 7 Days</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Since 14 April 25</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Last 30 Days</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Last 7 Days</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Next 7 Days</th>
            </tr>
          </thead>
          <tbody>
            {children.map((c) => (
              <tr key={c.code}>
                {stats.type !== 'ward'
                  ? (
                    <td><Link to={`/${c.type}/${c.code}/participation`}>{c.name}</Link><ResponsiblePersons persons={c.responsiblePersons} /></td>
                    )
                  : (
                    <td>{c.name}</td>
                    )}
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center font-weight-bold'>{formatInt(c.activity.personTypeAttendance?.personCount)}</td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(c.activity.personTypeAttendance?.current?.within, c.activity.personTypeAttendance?.personCount)} /></td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(c.activity.personTypeAttendance?.last30days?.within, c.activity.personTypeAttendance?.personCount)} /></td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(c.activity.personTypeAttendance?.last7days?.within, c.activity.personTypeAttendance?.personCount)} /></td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(c.activity.personTypeAttendance?.pubRepsAttending?.within)}</td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(c.activity.personTypeAttendance?.current?.outwith, c.activity.personTypeAttendance?.personCount)} /></td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(c.activity.personTypeAttendance?.last30days?.outwith, c.activity.personTypeAttendance?.personCount)} /></td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(c.activity.personTypeAttendance?.last7days?.outwith, c.activity.personTypeAttendance?.personCount)} /></td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(c.activity.personTypeAttendance?.pubRepsAttending?.outwith)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='font-weight-bold'>
              <td>Total</td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center font-weight-bold'>{formatInt(stats.activity.personTypeAttendance.personCount)}</td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(stats.activity.personTypeAttendance?.current?.within, stats.activity.personTypeAttendance?.personCount)} /></td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(stats.activity.personTypeAttendance?.last30days?.within, stats.activity.personTypeAttendance?.personCount)} /></td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(stats.activity.personTypeAttendance?.last7days?.within, stats.activity.personTypeAttendance?.personCount)} /></td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(stats.activity.personTypeAttendance?.pubRepsAttending?.within)}</td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(stats.activity.personTypeAttendance?.current?.outwith, stats.activity.personTypeAttendance?.personCount)} /></td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(stats.activity.personTypeAttendance?.last30days?.outwith, stats.activity.personTypeAttendance?.personCount)} /></td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'><NumPercCell value={formatNumAndPerc(stats.activity.personTypeAttendance?.last7days?.outwith, stats.activity.personTypeAttendance?.personCount)} /></td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(stats.activity.personTypeAttendance?.pubRepsAttending?.outwith)}</td>
            </tr>
          </tfoot>
        </Table>
      </>
    )
  }
}
